/**
 * All user-facing copy for the auth screens, centralized for future i18n.
 * Ported from the RSM portal so the two applications keep identical wording;
 * only the application name differs.
 */

export const AUTH_STRINGS = {
  // Brand
  workshopName: "Wagon Repair Workshop, Vadlapudi",
  railwayName: "South Coast Railway",
  appName: "Wheel Management System",
  appNameLong: "Wheel Management System Portal",
  ministryAttribution: "Ministry of Railways, Government of India",
  copyrightLine: (year: number) =>
    `© ${year} Wagon Repair Workshop, Vadlapudi - South Coast Railway. All rights reserved.`,

  // Welcome blurb (split so "Authorized access only." can be styled red+bold)
  welcomeIntro:
    "Welcome to the official Wheel Management System of the Wagon Repair Workshop, Vadlapudi (South Coast Railway). ",
  welcomeWarning: "Authorized access only.",
  welcomeOutro: " Misuse is subject to disciplinary or legal action.",

  // Security notice (footer banner)
  securityNoticeTitle: "Security Notice",
  securityNoticeBody:
    "This system is monitored for authorized use only. All activities are logged as per Indian Railways IT Security Policy, CERT-In advisories, and internal audit requirements.",

  // Footer links
  footerTerms: "Terms & Conditions",
  footerPrivacy: "Privacy Policy",
  footerContact: "Contact Us",

  // ── Login page ──
  loginTitle: "Sign In",
  loginPfNumberLabel: "PF Number",
  loginPasswordLabel: "Password",
  loginRememberMe: "Remember me",
  loginSubmit: "Sign In",
  loginSubmitting: "Signing in…",
  loginNoPasswordPrompt: "Don't have Password?",
  loginCreatePasswordCta: "Create Password",
  loginForgotPasswordCta: "Forgot Password?",

  // Field hints (always visible, small grey)
  loginPfHint: "Full PF number as printed on your ID card",
  loginPasswordHint: "Enter your account password (contact your administrator if you forgot it)",

  // Login errors
  loginPfRequired: "Please enter your PF number",
  loginPfInvalid: "PF Number contains invalid characters",
  loginPasswordRequired: "Please enter your password",
  loginPfTooShort:
    "This PF number looks too short — please type the full number from your ID card.",
  loginInvalidCredentials: "The PF number or password is not correct.",
  loginAccountNotFound:
    "This PF number is not registered yet. Please check you have typed the full PF number exactly as printed on your ID card. If it is correct, send your name, PF number, mobile number and shop to your administrator and your login will be created.",
  loginBadPassword: (attemptsLeft?: number): string => {
    const base = "The password is not correct. Please try again or contact your administrator.";
    if (typeof attemptsLeft === "number" && attemptsLeft > 0 && attemptsLeft <= 2) {
      return `${base} You have ${attemptsLeft} ${
        attemptsLeft === 1 ? "try" : "tries"
      } left before the account is locked for some time.`;
    }
    return base;
  },
  loginAccountInactive: "Your account has been disabled. Please contact your administrator.",
  loginAccountLocked: (until: string) =>
    `Your account is locked for a short time because of repeated wrong attempts. Please try again after ${until}. If you have forgotten your password, contact your administrator.`,
  loginShopNotAssigned:
    "No shop is mapped to your profile yet. Please ask your administrator to assign your shop and section, then sign in again.",
  loginShopNotOnboarded: (shopName?: string) =>
    `${
      shopName && shopName.trim() ? shopName : "Your shop"
    } is not activated in the system yet. Planning for your shop is being set up — you will be able to sign in once it is ready. Nothing is needed from your side.`,
  loginTooManyAttempts: (minutes: number) =>
    `Too many attempts. Please wait ${minutes} ${
      minutes === 1 ? "minute" : "minutes"
    } and try again.`,
  loginTimeout:
    "The server is busy or your connection dropped. Please wait a few seconds and press sign in once — there is no need to type your details again.",
  loginServerError:
    "Something went wrong on our side. Please try again in a minute. If it keeps happening, tell your administrator the time it happened.",
  loginPasswordNotSetBanner:
    "You haven't set your password yet. Please use the link sent to you, or contact your administrator.",

  // Session expiry (global interceptor)
  sessionExpiredMessage:
    "Your session has expired. Please sign in again — your saved work is safe.",

  // Forgot password (reached from the Forgot / Create Password links)
  forgotTitle: "Forgot Password",
  forgotIntro:
    "Enter your PF number. If your account has an email address, we will send you a link to set a new password.",
  forgotSubmit: "Send Reset Link",
  forgotSubmitting: "Sending…",
  forgotNoEmailHint:
    "No email after a few minutes? Check your spam folder, or ask your administrator to reset your password.",
  backToSignIn: "Back to Sign In",

  // Reset / create password (emailed link)
  resetTitle: "Reset Password",
  resetIntro: "Choose a new password for your account.",
  setupTitle: "Create Your Password",
  setupIntro: "Choose the password you will use to sign in to your new account.",
  resetNewPasswordLabel: "New Password",
  resetConfirmPasswordLabel: "Confirm Password",
  resetPasswordsDoNotMatch: "The two passwords do not match",
  resetSubmit: "Save Password",
  resetSubmitting: "Saving…",
  resetDone: "Password saved. Please sign in with your new password.",
  resetLinkInvalid: "This link is invalid or has expired. Please request a new one.",
  resetRequestNewLink: "Request a new link",
} as const;
